import axios from 'axios'
import {
  CreateOrUpdateTransitOperationRequest,
  CustomsRequest,
  TransitOperationDuplicationRequest,
  TransitOperationResponse,
} from '../../../../api/models'
import { DeclarationValidationRequest, DeclarationValidationRequestPhaseIdEnum } from '../../../../api/models/declaration-validation-request'
import { DeclarationValidationResponse } from '../../../../api/models/declaration-validation-response'
import { GuaranteeCalculationResponse } from '../../../../api/models/guarantee-calculation-response'
import { TransitOperationRestrictionCheckResponse } from '../../../../api/models/transit-operation-restriction-check-response'

const apiUrl = process.env.REACT_APP_API_URL

const apiConfig = {
  v2: {
    baseUrl: `${apiUrl}/v2/transit-operation`,
  },
  v3: {
    baseUrl: `${apiUrl}/v3/transit-operation`,
  },
}

export async function fetchTransitOperationV2(id: number, opts?: { signal?: AbortSignal }) {
  const { data } = await axios.get<TransitOperationResponse>(`${apiConfig.v2.baseUrl}/${id}`, {
    signal: opts?.signal,
  })

  return data
}

export async function fetchEnsTransitOperationV3(id: number, opts?: { signal?: AbortSignal }) {
  const { data } = await axios.get<TransitOperationResponse>(`${apiConfig.v3.baseUrl}/ENS/${id}`, {
    signal: opts?.signal,
  })

  return data
}

export async function createTransitOperationV2(request: CreateOrUpdateTransitOperationRequest) {
  const { data } = await axios.post<TransitOperationResponse>(`${apiConfig.v2.baseUrl}`, request)

  return data
}

export async function updateTransitOperationV2(id: number, request: CreateOrUpdateTransitOperationRequest) {
  const { data } = await axios.put<TransitOperationResponse>(`${apiConfig.v2.baseUrl}/${id}`, request)

  return data
}

export async function deleteTransitOperationV2(id: number) {
  const { data } = await axios.delete<void>(`${apiConfig.v2.baseUrl}/${id}`)

  return data
}

export async function validateTransitOperationV3(id: number, request: DeclarationValidationRequest, scope?: string) {
  const { data } = await axios.post<DeclarationValidationResponse>(`${apiConfig.v3.baseUrl}/${id}/validation`, request, {
    params: {
      scope,
    },
  })

  return data
}

export async function validateRestrictionsV3(id: number) {
  const { data } = await axios.get<TransitOperationRestrictionCheckResponse>(`${apiConfig.v3.baseUrl}/${id}/validation/restrictions`)

  return data
}

export async function sendSubmission(request: CustomsRequest) {
  return await axios.post<TransitOperationResponse>(`${apiConfig.v3.baseUrl}/customs`, request)
}

export async function amendSubmission(request: CustomsRequest) {
  return await axios.post<void>(`${apiConfig.v3.baseUrl}/customs/amend`, request)
}

export async function cancelSubmissionV2(request: { id: number; reason: string }) {
  return await axios.post<void>(`${apiConfig.v2.baseUrl}/customs/cancel`, request)
}

export async function duplicateSubmission(request: TransitOperationDuplicationRequest) {
  const { data } = await axios.post<TransitOperationResponse>(`${apiConfig.v3.baseUrl}/duplicate`, request)

  return data
}

export async function createTransitOperationFromEnsV2(request: TransitOperationDuplicationRequest) {
  const { data } = await axios.post<TransitOperationResponse>(`${apiConfig.v2.baseUrl}/duplicate/ens`, request)

  return data
}

export async function requestRelease(request: { id: number; isAcceptedRelease: boolean }, opts?: { signal?: AbortSignal }) {
  await axios.post<void>(`${apiConfig.v2.baseUrl}/customs/release`, request, {
    signal: opts?.signal,
  })
}

export async function releaseNonArrivedMovement(request: { id: number; explanation: string }, opts?: { signal?: AbortSignal }) {
  await axios.post<void>(`${apiConfig.v2.baseUrl}/customs/non-arrived-movement-release`, request, {
    signal: opts?.signal,
  })
}

export async function forceTransitOperationStatusV2(id: number) {
  await axios.patch<void>(`${apiConfig.v2.baseUrl}/status/${id}`)
}

export async function sendCustomsResponseV2(request: {
  id: number
  isAccepted: boolean
  notificationDescription: string
}, opts?: { signal?: AbortSignal }) {
  await axios.post<void>(`${apiConfig.v2.baseUrl}/customs/response`, request, {
    signal: opts?.signal,
  })
}

export async function contactCustomsV2(request: {
  id: number
  notificationDescription: string
  notificationReason: string
}, opts?: { signal?: AbortSignal }) {
  await axios.post<void>(`${apiConfig.v2.baseUrl}/customs/contact`, request, {
    signal: opts?.signal,
  })
}

export async function submitPresentationNotification(request: CustomsRequest) {
  return await axios.post<void>(`${apiConfig.v2.baseUrl}/customs/presentation-notification`, request)
}

export async function submitEnsPresentationNotification(request: CustomsRequest) {
  return await axios.post<void>(`${apiConfig.v2.baseUrl}/customs/ics-presentation-notification`, request)
}

export async function fetchSubmissionMessageV2(request: CustomsRequest) {
  return await axios.post<Blob>(`${apiConfig.v2.baseUrl}/customs/generate`, request, { responseType: 'blob' })
}

export async function fetchGuaranteeCalculationV3(transitionOperationId: number, exchangeRate: number) {
  return await axios.get<GuaranteeCalculationResponse>(`${apiUrl}/v3/guarantee-calculation/${transitionOperationId}`, {
    params: { exchangeRate },
  })
}

export async function fetchGuaranteeCalculationFile(request: { id: number; xlsx: boolean }) {
  return await axios.get<Blob>(`${apiUrl}/v3/guarantee-calculation/${request.id}`, {
    headers: {
      Accept: request.xlsx ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf',
    },
    responseType: 'blob',
  })
}

export async function fetchTSADPdf(transitionOperationId: number, phaseId?: DeclarationValidationRequestPhaseIdEnum) {
  return await axios.get<Blob>(`${apiUrl}/v3/pdf/${transitionOperationId}`, {
    params: { phaseId },
    responseType: 'blob',
  })
}
